import { apiClient, unwrap } from '@/lib/apiClient';
import type { ApiSuccess } from '@/types/api';
import type {
  BroadcastDateRange,
  BroadcastDeliveryStatus,
  BroadcastDetail,
  BroadcastMetrics,
  BroadcastPage,
  BroadcastRecipient,
  BroadcastRecipientKind,
  BroadcastResult,
  BroadcastStatus,
  BroadcastSummary,
  MessageAudience,
  MessageChannel,
  SendBroadcastPayload,
} from '@/types/messaging';

/**
 * Every call the notifications screen makes.
 *
 * Components never reach for axios themselves (CLAUDE.md §6). There is still no
 * endpoint here that takes a mobile number: a send names an audience or names
 * students by their public id, and the server looks the numbers up on its own
 * roll — so nothing this file can be made to do reaches anybody outside the
 * institute.
 */

export interface BroadcastListParams {
  page: number;
  limit: number;
  search?: string;
  channel?: MessageChannel;
  status?: BroadcastStatus;
  templateKey?: string;
  includes?: BroadcastRecipientKind;
  sectionUuid?: string;
  dateRange: BroadcastDateRange;
}

export interface RecipientListParams {
  page: number;
  limit: number;
  search?: string;
  status?: BroadcastDeliveryStatus;
}

/** Drops the keys the office has not filled in, so no `?channel=` is sent. */
function clean(params: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== ''),
  );
}

export const notificationsApi = {
  /**
   * Who a send would reach, plus the classes and templates the compose card
   * offers. Narrowed to one section when the office picks one, so the count
   * under the card follows what they have chosen.
   */
  audience: (sectionUuid?: string) =>
    unwrap(
      apiClient.get<ApiSuccess<MessageAudience>>('/institute/messaging/audience', {
        params: clean({ sectionUuid }),
      }),
    ),

  metrics: () =>
    unwrap(apiClient.get<ApiSuccess<BroadcastMetrics>>('/institute/messaging/metrics')),

  send: (payload: SendBroadcastPayload) =>
    unwrap(apiClient.post<ApiSuccess<BroadcastResult>>('/institute/messaging/broadcast', payload)),

  list: (params: BroadcastListParams) =>
    unwrap(
      apiClient.get<ApiSuccess<BroadcastPage<BroadcastSummary>>>(
        '/institute/messaging/broadcasts',
        { params: clean(params as unknown as Record<string, unknown>) },
      ),
    ),

  get: (uuid: string) =>
    unwrap(
      apiClient.get<ApiSuccess<BroadcastDetail>>(`/institute/messaging/broadcasts/${uuid}`),
    ),

  recipients: (uuid: string, params: RecipientListParams) =>
    unwrap(
      apiClient.get<ApiSuccess<BroadcastPage<BroadcastRecipient>>>(
        `/institute/messaging/broadcasts/${uuid}/recipients`,
        { params: clean(params as unknown as Record<string, unknown>) },
      ),
    ),
};
